import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const usePaymentStore = defineStore('payments', () => {
  const uiStore = useUIStore()

  // State
  const payments = ref([])
  const currentPayment = ref(null)
  const loading = ref(false)

  // Payment gateways
  const paymentGateways = [
    { value: 'stripe', label: 'Stripe', icon: 'stripe-icon.svg' },
    { value: 'paypal', label: 'PayPal', icon: 'paypal-icon.svg' },
    { value: 'razorpay', label: 'Razorpay', icon: 'razorpay-icon.svg' }
  ]

  // Payment statuses
  const paymentStatuses = [
    { value: 'pending', label: 'Pending', color: 'yellow' },
    { value: 'processing', label: 'Processing', color: 'blue' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'failed', label: 'Failed', color: 'red' },
    { value: 'refunded', label: 'Refunded', color: 'gray' }
  ]

  // API Functions
  const fetchPayments = async (params = {}) => {
    try {
      loading.value = true
      logService.info('Fetching payments...', params)

      const response = await apiService.get('/payments', params)

      if (response.success) {
        payments.value = response.data
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch payments')
      }
    } catch (error) {
      logService.error('Fetch Payments Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { data: [], success: false }
    } finally {
      loading.value = false
    }
  }

  const fetchPayment = async (id) => {
    try {
      loading.value = true
      logService.info(`Fetching payment: ${id}`)

      const response = await apiService.get(`/payments/${id}`)

      if (response.success) {
        currentPayment.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Payment not found')
      }
    } catch (error) {
      logService.error(`Fetch Payment Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const processPayment = async (paymentData) => {
    try {
      loading.value = true
      logService.info('Processing payment...', paymentData)

      const response = await apiService.post('/payments/process', paymentData)

      if (response.success) {
        payments.value.unshift(response.data)
        currentPayment.value = response.data

        uiStore.showNotification({
          type: 'success',
          title: 'Payment Processed',
          message: response.message
        })

        return response.data
      } else {
        throw new Error(response.message || 'Payment processing failed')
      }
    } catch (error) {
      logService.error('Process Payment Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Payment Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const refundPayment = async (id, refundData) => {
    try {
      loading.value = true
      logService.info(`Refunding payment: ${id}`, refundData)

      const response = await apiService.post(`/payments/${id}/refund`, refundData)

      if (response.success) {
        const index = payments.value.findIndex(payment => payment.id === id)
        if (index !== -1) {
          payments.value[index] = response.data
        }

        if (currentPayment.value?.id === id) {
          currentPayment.value = response.data
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Payment Refunded',
          message: 'Payment has been refunded successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to refund payment')
      }
    } catch (error) {
      logService.error(`Refund Payment Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Refund Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  // Getters
  const getPaymentById = (id) => {
    return payments.value.find(payment => payment.id === id)
  }

  const getPaymentsByStatus = (status) => {
    return payments.value.filter(payment => payment.status === status)
  }

  const getCompletedPayments = () => {
    return payments.value.filter(payment => payment.status === 'completed')
  }

  const getPendingPayments = () => {
    return payments.value.filter(payment => payment.status === 'pending')
  }

  const getTotalRevenue = () => {
    return payments.value
      .filter(payment => payment.status === 'completed')
      .reduce((total, payment) => total + parseFloat(payment.amount || 0), 0)
  }

  const getPaymentsByGateway = (gateway) => {
    return payments.value.filter(payment => payment.payment_gateway === gateway)
  }

  return {
    // State
    payments,
    currentPayment,
    loading,
    paymentGateways,
    paymentStatuses,

    // API Actions
    fetchPayments,
    fetchPayment,
    processPayment,
    refundPayment,

    // Getters
    getPaymentById,
    getPaymentsByStatus,
    getCompletedPayments,
    getPendingPayments,
    getTotalRevenue,
    getPaymentsByGateway
  }
})
